import { IconNames } from '@blueprintjs/icons';
import { formatDate } from '@strata/helper-functions';
import { GridWithFilters, PrimaryData, SecondaryData } from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';

import { ListPageHeader } from 'components/ListPageHeader';
import { ProgramRuleStatusIndicator } from 'pages/programManagement/ProgramRuleStatusIndicator';
import { CREATE_ROUTE, MAX_STANDARD_SKU_ID } from 'services/constants';
import { useGet } from 'services/Http';

/** Gets the indicator status for a program rule based on the rule data */
const getProgramRuleStatus = (programRule) => {
    if (!programRule.program_status) {
        return ProgramRuleStatusIndicator.Statuses.INACTIVE;
    }
    if (dayjs().isAfter(programRule.sku_end_date)) {
        return ProgramRuleStatusIndicator.Statuses.EXPIRED;
    }
    if (dayjs().isBefore(programRule.sku_start_date)) {
        return ProgramRuleStatusIndicator.Statuses.PEND_START;
    }
    return ProgramRuleStatusIndicator.Statuses.ACTIVE;
};

const columns = ({ navigate }) => [
    {
        header: 'Program Name',
        accessorKey: 'program_name',
        size: 200,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        header: 'Description',
        accessorKey: 'program_description',
        size: 200,
    },
    {
        header: 'SKU',
        accessorKey: 'pharmacy_sku_id',
        size: 55,
        cell: ({ getValue }) => <SecondaryData data={getValue().toString().padStart(4, '0')} />,
    },
    {
        header: 'Program Start Date',
        accessorKey: 'sku_start_date',
        size: 125,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        header: 'Program End Date',
        accessorKey: 'sku_end_date',
        size: 125,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        header: 'Status',
        accessorKey: 'programRuleStatus',
        size: 100,
        enableSorting: false,
        cell: ({ getValue }) => <ProgramRuleStatusIndicator status={getValue()} />,
    },
    {
        header: 'Actions',
        accessorKey: 'actions',
        size: 70,
        enableSorting: false,
        cell: ({ row }) => (
            <div className="sds_filter-list__actions">
                <Icon
                    icon={
                        row.original.pharmacy_sku_id > MAX_STANDARD_SKU_ID
                            ? IconNames.EDIT
                            : IconNames.EYE_OPEN
                    }
                    intent={Icon.Intent.SECONDARY}
                    title={row.original.pharmacy_sku_id > MAX_STANDARD_SKU_ID ? 'Edit' : 'View'}
                    onClick={() =>
                        navigate({
                            pathname: `/program-management/program-rules/${row.original.pharmacy_sku_id}`,
                        })
                    }
                />
            </div>
        ),
    },
];

export const ProgramRules = () => {
    const navigate = useNavigate();
    const programRules = useGet('/finance/skus');

    const data = programRules?.map((programRule) => ({
        ...programRule,
        programRuleStatus: getProgramRuleStatus(programRule),
    }));

    return (
        <div className="sds_filter-list">
            <GridWithFilters
                headerContent={
                    <ListPageHeader
                        pageTitle="Program Rules"
                        buttons={[
                            {
                                label: 'Create Program',
                                path: `/program-management/program-rules/${CREATE_ROUTE}`,
                            },
                        ]}
                    />
                }
                enableSearch
                isLoading={!programRules}
                data={data}
                sortProperties={[{ id: 'pharmacy_sku_id', desc: true }]}
                filterCategories={[
                    {
                        key: 'programRuleStatus',
                        title: 'Status',
                        customValues: Object.entries(ProgramRuleStatusIndicator.StatusMessages).map(
                            ([value, text]) => ({ value, text }),
                        ),
                        defaultValue: [
                            ProgramRuleStatusIndicator.Statuses.ACTIVE,
                            ProgramRuleStatusIndicator.Statuses.PEND_START,
                        ],
                    },
                ]}
                columns={columns({ navigate })}
                rowCssClass={(rowData) =>
                    classNames({
                        'griddle-row--disabled': [
                            ProgramRuleStatusIndicator.Statuses.EXPIRED,
                            ProgramRuleStatusIndicator.Statuses.INACTIVE,
                        ].includes(rowData.programRuleStatus),
                    })
                }
            />
        </div>
    );
};
